/**
 * OverviewTile — Aperçu entreprise (Batch #14.243 / #14.314).
 * Chiffres = grand livre REPORTÉ du dossier (revenus, dépenses, résultat, TPS/TVQ des ventes & achats) ;
 * les pièces reçues du portail (factures, reçus, trajets) se traitent dans Files PMS › Entreprise.
 */
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { TrendingUp, TrendingDown, Wallet, Car, Percent, Inbox, BookOpen, AlertTriangle } from "lucide-react";
import { Link } from "react-router-dom";
import { fmtCAD, KpiCard, MONTHS_FR } from "./_shared";
import EntrepriseSettingsCard from "./EntrepriseSettingsCard";

const booksPath = (m, tile, tab) => `/admin/tenue-livres/${m}/${tile}${tab ? `?tab=${tab}` : ""}`;

function PendingBanner({ pending }) {
  if (!pending || pending.total === 0) return null;
  const parts = [[pending.revenues, "facture(s) de revenus", "ent_revenues"], [pending.expenses, "dépense(s) / reçu(s)", "ent_expenses"], [pending.mileage, "trajet(s)", "ent_mileage"]].filter(([n]) => n > 0);
  return (
    <div className="flex flex-col sm:flex-row sm:items-center gap-3 bg-rose-50 border border-rose-200 rounded-xl px-4 py-3" data-testid="ent-pending-banner">
      <Inbox className="w-5 h-5 text-rose-600 flex-shrink-0" />
      <div className="flex-1 text-sm text-rose-900">
        <strong data-testid="ent-pending-unseen">{pending.total} pièce(s) reçue(s) du portail à traiter</strong>
        <span className="text-xs"> — {parts.map(([n, l, tab], i) => <span key={tab}>{i > 0 && " · "}<Link to={`/admin/pms-queue?tab=${tab}`} className="underline" data-testid={`ent-pending-link-${tab}`}>{n} {l}</Link></span>)}</span>
      </div>
      <Link to="/admin/pms-queue?tab=ent_expenses" className="inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg bg-white border border-rose-300 text-rose-800 text-xs font-semibold hover:bg-rose-100" data-testid="ent-pending-open-pms">
        Ouvrir Files PMS › Entreprise
      </Link>
    </div>
  );
}

function RecentDocs({ title, items, to, testId, empty }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4" data-testid={testId}>
      <div className="flex items-center justify-between mb-2">
        <h4 className="text-xs font-bold uppercase tracking-wider text-slate-600">{title}</h4>
        <Link to={to} className="text-[11px] text-teal-700 hover:underline">Ouvrir</Link>
      </div>
      {items.length === 0 ? <p className="text-xs text-slate-400 italic">{empty}</p> : (
        <ul className="space-y-1">{items.map((d) => (
          <li key={d.id} className="text-xs text-slate-700 flex items-center gap-2">
            <span className="text-slate-400 tabular-nums">{d.date}</span>
            <span className="flex-1 truncate">{d.number && <span className="font-mono text-slate-500 mr-1">{d.number}</span>}{d.party_name}{d.description ? ` · ${d.description}` : ""}</span>
            {d.draft && <span className="text-[9px] px-1 py-0.5 rounded bg-orange-100 text-orange-800 font-semibold">non reporté</span>}
            <span className="font-mono">{fmtCAD(d.total)}</span>
          </li>
        ))}</ul>
      )}
    </div>
  );
}

export default function OverviewTile({ masterId, overview, onChanged }) {
  const t = overview.totals;
  const tx = overview.taxes;
  const draftsTotal = Object.values(overview.drafts || {}).reduce((s, n) => s + (n || 0), 0);
  const chart = t.monthly.map((m) => ({ m: MONTHS_FR[m.m - 1], Revenus: m.revenues, Dépenses: m.expenses }));
  const fy = overview.fiscal_year;
  return (
    <div className="space-y-4" data-testid="ent-overview">
      <PendingBanner pending={overview.pending} />
      {!overview.nb_accounts && (
        <div className="flex items-center gap-2 bg-orange-50 border border-orange-200 rounded-xl px-4 py-2.5 text-xs text-orange-900" data-testid="ent-no-plan">
          <BookOpen className="w-4 h-4" /> Aucun plan comptable dans ce dossier — les chiffres restent à zéro tant que la tenue de livres n'est pas démarrée.
          <Link to={booksPath(masterId, "plan")} className="ml-auto underline font-semibold">Installer le plan comptable</Link>
        </div>
      )}
      {draftsTotal > 0 && (
        <div className="flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-xl px-4 py-2.5 text-xs text-amber-900" data-testid="ent-drafts-note">
          <AlertTriangle className="w-4 h-4" /> {draftsTotal} pièce(s) non reportée(s) en tenue de livres — non comptées ci-dessous (grand livre reporté seulement).
          <Link to={booksPath(masterId, "transactions-clients")} className="ml-auto underline font-semibold">Transactions clients</Link>
          <Link to={booksPath(masterId, "transactions-fournisseurs")} className="underline font-semibold">Transactions fournisseurs</Link>
        </div>
      )}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        <KpiCard label="Revenus (hors taxes)" value={fmtCAD(t.revenues)} sub="comptes de revenus · grand livre" tone="emerald" icon={TrendingUp} testId="ent-kpi-revenues" />
        <KpiCard label="Dépenses" value={fmtCAD(t.expenses)} sub="comptes de dépenses · grand livre" tone="rose" icon={TrendingDown} testId="ent-kpi-expenses" />
        <KpiCard label="Résultat" value={fmtCAD(t.net)} sub="Revenus − Dépenses" tone={t.net >= 0 ? "blue" : "amber"} icon={Wallet} testId="ent-kpi-net" />
        <KpiCard label="Kilométrage (portail)" value={`${t.km.toLocaleString("fr-CA")} km`} sub={`≈ ${fmtCAD(t.km_amount)} à ${t.km_rate} $/km · ${t.nb_mileage} trajet(s)`} tone="amber" icon={Car} testId="ent-kpi-km" />
        <KpiCard label="TPS/TVQ nettes" value={fmtCAD(tx.total_net)} sub={tx.drafts_total ? `${tx.drafts_total} pièce(s) non reportée(s) exclue(s)` : "à remettre (ventes − achats reportés)"} tone="slate" icon={Percent} testId="ent-kpi-taxes" />
      </div>

      <div className="bg-white border border-slate-200 rounded-xl p-5">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <h3 className="text-sm font-bold text-slate-800">Revenus vs Dépenses — {overview.year}</h3>
          <span className="text-[11px] text-slate-500" data-testid="ent-fy-range">Exercice {fy.from} → {fy.to} · grand livre reporté</span>
        </div>
        <div className="min-w-0" data-testid="ent-monthly-chart">
          <ResponsiveContainer width="100%" height={224}>
            <BarChart data={chart} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis dataKey="m" tick={{ fontSize: 10, fill: "#64748b" }} />
              <YAxis tick={{ fontSize: 10, fill: "#64748b" }} />
              <Tooltip formatter={(v) => fmtCAD(v)} contentStyle={{ fontSize: 11, borderRadius: 6 }} />
              <Bar dataKey="Revenus" fill="#10b981" radius={[4, 4, 0, 0]} />
              <Bar dataKey="Dépenses" fill="#f43f5e" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="grid md:grid-cols-3 gap-3">
        <RecentDocs title="Dernières ventes" items={overview.recent.sales} to={booksPath(masterId, "transactions-clients", "ventes")} testId="ent-recent-sales" empty="Aucune vente en tenue de livres" />
        <RecentDocs title="Derniers achats" items={overview.recent.purchases} to={booksPath(masterId, "transactions-fournisseurs", "achats")} testId="ent-recent-purchases" empty="Aucun achat en tenue de livres" />
        <div className="bg-white border border-slate-200 rounded-xl p-4" data-testid="ent-recent-mileage">
          <div className="flex items-center justify-between mb-2">
            <h4 className="text-xs font-bold uppercase tracking-wider text-slate-600">Derniers trajets (portail)</h4>
            <Link to="/admin/pms-queue?tab=ent_mileage" className="text-[11px] text-teal-700 hover:underline">Files PMS</Link>
          </div>
          {overview.recent.mileage.length === 0 ? <p className="text-xs text-slate-400 italic">Aucun trajet reçu</p> : (
            <ul className="space-y-1">{overview.recent.mileage.map((k) => (
              <li key={k.id} className="text-xs text-slate-700 flex items-center gap-2"><span className="text-slate-400 tabular-nums">{k.trip_date}</span><span className="flex-1 truncate">{k.from_location} → {k.to_location}</span><span className="font-mono">{k.km_distance} km</span></li>
            ))}</ul>
          )}
        </div>
      </div>

      <EntrepriseSettingsCard masterId={masterId} settings={overview.settings} onSaved={onChanged} />
    </div>
  );
}
